import React, { memo, useState, useEffect, useCallback, useMemo } from 'react';
import { Handle, Position, useNodes, useEdges, useReactFlow } from 'reactflow';
import { FiGitMerge, FiShuffle, FiPlus, FiX } from 'react-icons/fi';
import { v4 as uuidv4 } from 'uuid';
import NodeWrapper from './NodeWrapper';
import { COMPARATOR_OPS, defaultLogicInputs, resolveDisplayName, resolveNameDetail } from '../utils/nodeUtils';
import { getNodeConfig, getNodeAccent, isNodeShownOnChart, buildNodeVisualTogglePatch } from '../config/nodeVisualConfig';
import { useNodeVisualOverrides } from '../../../../../hooks/useVisualOverrides';

const LogicNode = memo(({ id, type: registeredType, data, selected }) => {
  const { setNodes } = useReactFlow();
  //* 50-8 R1-T3/R2-T4 — display keys read/write through the ONE mounted visual-overrides store;
  //* 50-21 R10 — `record` is the node-id -> entry map the display-name resolver reads.
  const { effectiveData, write: writeDisplay, record: displayRecord } = useNodeVisualOverrides(id, data);
  const allNodes = useNodes();
  const allEdges = useEdges();

  // --> Local State (Synced with data)
  const [operator, setOperator] = useState(data.operator || 'and');
  const [inputs, setInputs] = useState(() => {
    const baseInputs = data.inputs || defaultLogicInputs(data.operator);
    return baseInputs.map((input, idx) => ({
      ...input,
      id: input.id || `input_${idx}` // Keep deterministic fallback for init
    }));
  });

  // --> Optimization: Memoize incoming edges map for O(1) lookup
  const incomingEdges = useMemo(() => {
      const map = new Map();
      allEdges.forEach(edge => {
          if (edge.target === id) {
              map.set(edge.targetHandle, edge);
          }
      });
      return map;
  }, [allEdges, id]);

  const isComparator = Object.prototype.hasOwnProperty.call(COMPARATOR_OPS, operator);
  // Classification accent: comparator → indigo, logic gate → fuchsia
  const accent = getNodeAccent(isComparator ? 'comparator' : 'logic_gate', data);
  const operatorGlyph = COMPARATOR_OPS[operator]?.glyph
      || (operator === 'and' ? 'AND &' : operator === 'or' ? 'OR |' : operator === 'not' ? 'NOT !' : unescape(operator || '').toUpperCase());
  const operatorToken = operatorGlyph.split(' ').pop();

  // --> Unified Update Logic
  const updateNode = useCallback((newOp, newInputs) => {
    setNodes((nds) => nds.map((node) => {
        if (node.id === id) {
            return {
                ...node,
                data: {
                    ...node.data,
                    operator: newOp,
                    inputs: newInputs,
                    //! 50-21 R11-T1 — NO `label` write: the title is resolved at read time
                    //! (resolveDisplayName); a stored legacy name is never overwritten.
                }
            };
        }
        return node;
    }));
  }, [id, setNodes]);

  useEffect(() => {
    if (data.operator !== undefined && data.operator !== operator) setOperator(data.operator);
    
    if (data.inputs !== undefined) {
      let nextInputs = [...data.inputs];
      let needsParentSync = false;

      // --> Migration: Ensure all inputs have IDs (Sync back to parent)
      nextInputs = nextInputs.map((input, idx) => {
          if (!input.id) {
              needsParentSync = true;
              return { ...input, id: `input_${idx}` };
          }
          return input;
      });

      // --> Defensive constraint: Ensure NOT only ever has 1 input locally
      if (data.operator === 'not' && nextInputs.length > 1) {
          nextInputs = [nextInputs[0]];
          needsParentSync = true;
      }
      
      if (needsParentSync) {
          // Sync corrected IDs back to global state
          updateNode(data.operator || operator, nextInputs);
          setInputs(nextInputs);
      } else if (JSON.stringify(nextInputs) !== JSON.stringify(inputs)) {
          setInputs(nextInputs);
      }
    }
  }, [data.operator, data.inputs, operator, inputs, updateNode]);

  const isUnary = operator === 'not';
  const isLogicGate = ['and', 'or', 'not'].includes(operator);
  


  const addInput = () => {
    // Generate unique ID for new input
    const newId = `input_${uuidv4()}`;
    const newInputs = [...inputs, { type: 'link', offset: 0, id: newId }];
    setInputs(newInputs);
    updateNode(operator, newInputs);
  };

  const removeInput = (idToRemove) => {
    if (inputs.length <= (isUnary ? 1 : 2)) return;
    const newInputs = inputs.filter((i) => i.id !== idToRemove);
    setInputs(newInputs);
    updateNode(operator, newInputs);
  };

  // --> Helper to Find Connected Source Details
  const getSourceInfo = useCallback((inputId) => {
      const edge = incomingEdges.get(inputId);
      
      if (!edge) return null;
      const sourceNode = allNodes.find(n => n.id === edge.source);
      
      // 50-21 R10 — the ONE display-name resolver (record entry for the source node, if any)
      let displayName = resolveDisplayName(sourceNode, allNodes, allEdges, displayRecord?.[sourceNode?.id], displayRecord);
      
      // Fallback if utility returns something generic (shouldn't happen often)
      if (['Comparator', 'Comparison', 'Logic Gate', 'Logical'].includes(displayName)) {
          if (sourceNode?.type === 'comparator') displayName = 'Comp';
          if (sourceNode?.type === 'logic_gate') displayName = (sourceNode?.data?.operator || 'Logic').toUpperCase();
      }

      return {
          id: sourceNode?.id,
          type: sourceNode?.type,
          name: displayName,
          timeframe: sourceNode?.data?.timeframe,
          field: edge.sourceHandle,
          data: sourceNode?.data
      };
  }, [incomingEdges, allNodes, allEdges, displayRecord]);

  // --> Title via the ONE resolver (50-21 R10-T2). The registered type decides comparator /
  //     logic_gate / time_window (time_window mounts this component too); local operator/inputs
  //     state is ahead of `data`, so the temp node carries it.
  const nodeType = registeredType || (isComparator ? 'comparator' : 'logic_gate');
  const tempNode = useMemo(() => ({
      id, type: nodeType, data: { ...data, operator, inputs },
  }), [id, nodeType, data, operator, inputs]);
  const smartTitle = useMemo(
      () => resolveDisplayName(tempNode, allNodes, allEdges, displayRecord?.[id], displayRecord),
      [tempNode, allNodes, allEdges, displayRecord, id],
  );
  //--> subtitle: the CURRENT auto text, shown only while a record custom_name is set
  const autoSubtitle = useMemo(
      () => resolveNameDetail(tempNode, allNodes, allEdges, displayRecord?.[id], displayRecord).autoText,
      [tempNode, allNodes, allEdges, displayRecord, id],
  );

  const renderInputRow = (input, idx) => {
      const { id: inputId, type, value, offset, field } = input;
      const sourceInfo = getSourceInfo(inputId);

      return (
          <div key={inputId} className="relative flex items-center gap-1.5 w-full bg-bg-3/50 px-2 py-1 rounded-md border border-[var(--divider)] h-[32px] group/row">
              {/* Connector Handle */}
              <Handle
                  type="target"
                  id={inputId}
                  position={Position.Left}
                  className="!w-2.5 !h-2.5 !border-2 !border-[var(--bg-2)] !left-[-16px] transition-transform hover:scale-125"
                  style={{ top: '50%', background: accent }}
              />

              {!isLogicGate && (
                  type === 'constant' ? (
                      <span className="flex-shrink-0 text-[7px] font-black px-1 rounded bg-[var(--badge-warn-bg)] text-[var(--badge-warn-fg)]">NUM</span>
                  ) : (
                      <span className="flex-shrink-0 text-[7px] font-black px-1 rounded bg-bg-3 text-fg-2">LNK</span>
                  )
              )}

              {type === 'constant' ? (
                  <span className="text-[10px] font-mono font-bold text-fg-1 truncate">
                      {value ?? '0'}
                  </span>
              ) : (
                      <span className={`text-[9px] font-bold truncate flex-1 flex items-center transition-all gap-1.5 ${sourceInfo ? 'text-fg-1' : 'text-fg-3 italic'}`}>
                          {sourceInfo?.timeframe && (
                              <span className="flex-shrink-0 px-1 py-0.5 rounded-[3px] bg-bg-3 text-fg-3 text-[6px] font-black border border-[var(--divider)] mb-[1px]">
                                  {sourceInfo.timeframe}
                              </span>
                          )}
                          {/* Use the smart name resolved by getSourceInfo -> resolveDisplayName */}
                          <span className="truncate" title={sourceInfo?.name}>
                              {sourceInfo?.name || 'Select Source...'}
                          </span>
                      {(sourceInfo?.field || field) && (
                          <span className="text-fg-2 font-mono uppercase text-[7px] font-black mb-[1px]">
                              .{sourceInfo?.field || field}
                          </span>
                      )}
                  </span>
              )}

              {/* Offset/Lookback Small Badge */}
              {type === 'link' && (
                  <span className={`font-mono text-[6px] font-black ml-auto ${offset > 0 ? 'text-warning' : 'text-fg-3 opacity-60'}`}>
                       {offset > 0 ? `${offset}P` : 'C'}
                  </span>
              )}

              {/* In-Row NOT Badge (Unary only) — accent-filled */}
              {isUnary && (
                <div className="flex-shrink-0 px-1 py-0.5 rounded shadow-sm flex items-center justify-center min-w-[24px] h-[16px]" style={{ background: accent }}>
                    <span className="font-black text-white text-[8px] leading-none mb-[0.2px]">NOT !</span>
                </div>
              )}

              {/* Remove Button (Visible on Hover, for 3+ inputs on non-unary) */}
              {!isUnary && !isComparator && inputs.length > 2 && (
                  <button
                      onClick={() => removeInput(inputId)}
                      className="ml-1 p-1 text-fg-3 hover:text-loss transition-colors opacity-0 group-hover/row:opacity-100"
                  >
                      <FiX size={10} />
                  </button>
              )}
          </div>
      );
  };

  // --> Chart Visibility Toggle (New Implicit Connection)
  const toggleChart = useCallback((e) => {
      e.stopPropagation();
      //! R1-T3 — ONE toggle helper + the ONE display writer; no direct `setNodes` for display keys.
      //  Default to a marker for logic nodes if type not set.
      writeDisplay?.(buildNodeVisualTogglePatch('logic_gate', effectiveData, 'visibility', {
          defaultType: isComparator || isLogicGate ? 'marker' : 'line',
      }));
  }, [writeDisplay, effectiveData, isComparator, isLogicGate]);

  return (
    <NodeWrapper
        selected={selected}
        type={smartTitle}
        accent={accent}
        icon={getNodeConfig(isComparator ? 'comparator' : 'logic_gate').icon || (isComparator ? FiShuffle : FiGitMerge)}
        className="w-[240px]"
        compact={true}
        onToggleChart={toggleChart}
        isChartVisible={isNodeShownOnChart('logic_gate', effectiveData)}
        isHighlighted={data.isHighlighted}
    >
        <div className="flex flex-col gap-1 -mt-1 relative">
            {autoSubtitle && (
                <div data-testid="condition-node-subtitle" className="text-[8px] text-fg-3 truncate" title={autoSubtitle}>
                    {autoSubtitle}
                </div>
            )}
            <div className="flex flex-col gap-1 relative">
                {inputs.map((input, idx) => (
                    <React.Fragment key={input.id || idx}>
                        {renderInputRow(input, idx)}
                        
                        {/* Centered Token between rows (for 2-row layout) — accent-filled */}
                        {idx === 0 && inputs.length === 2 && !isUnary && (
                             <div className="absolute top-[34px] left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 pointer-events-none">
                                <div className="px-2 py-0.5 rounded shadow-lg flex items-center justify-center min-w-[28px] pointer-events-auto h-[20px] whitespace-nowrap" style={{ background: accent }}>
                                    <span className="font-black text-white text-[10px] leading-none mb-[0.5px]">
                                        {operatorToken}
                                    </span>
                                </div>
                            </div>
                        )}

                        {/* Repeat Token for 3+ inputs (Inline between rows) — accent-filled */}
                        {idx < inputs.length - 1 && inputs.length > 2 && !isUnary && (
                            <div className="flex justify-center -my-1 z-10 pointer-events-none">
                                <div className="px-2 py-0.5 rounded-[4px] text-white text-[9px] font-black shadow-sm whitespace-nowrap min-w-[24px] flex items-center justify-center" style={{ background: accent }}>
                                    {operatorToken}
                                </div>
                            </div>
                        )}
                    </React.Fragment>
                ))}

                {/* Add Input Button (Logic Gates only, not NOT) */}
                {isLogicGate && !isUnary && (
                    <button
                        onClick={addInput}
                        className="flex items-center justify-center gap-1 w-full py-1 text-[8px] uppercase font-bold text-fg-3 hover:text-fg-1 transition-colors"
                    >
                        <FiPlus size={10} />
                        <span>Add Condition</span>
                    </button>
                )}

                {/* Live State Indicator (Output Value) — Three-state: true/false/unknown */}
                <div className="flex items-center justify-end gap-1.5 pt-1 border-t border-[var(--divider)] mt-1">
                    <span className="text-[7px] font-bold text-fg-3 uppercase tracking-wider">Output</span>
                    {/* --> Extract scalar from {value, is_valid} object shape (MTF warmup support) */}
                {(() => {
                    const resolved = typeof data.value === 'object' && data.value !== null ? data.value.value : data.value;
                    return (
                        <div
                            className="w-2 h-2 rounded-full transition-all duration-300"
                            style={{
                                background: resolved === true ? 'var(--color-profit)'
                                    : resolved === false ? 'var(--color-loss)'
                                    : 'var(--fg-3)',
                                transform: resolved === true ? 'scale(1.1)' : 'scale(1)',
                            }}
                        />
                    );
                })()}
                </div>
            </div>
        </div>

        {/* Source Handle (Output) — accent-tinted */}
        <Handle
            type="source"
            position={Position.Right}
            className="!w-2.5 !h-2.5 !border-2 !border-[var(--bg-2)] !right-[-8px] transition-transform hover:scale-125"
            style={{ top: '50%', background: accent }}
        />
    </NodeWrapper>
  );
});

export default LogicNode;
